import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MarketTicker } from "@/components/MarketTicker";

/**
 * NewsTicker — the marquee strap that sits between the primary nav and
 * the editorial content. Admin-toggled between four modes via
 * /api/site-config/ticker:
 *
 *   markets   → existing live Bloomberg-style market ticker
 *   breaking  → red BREAKING NEWS strap with a single admin-set headline
 *   latest    → black LATEST NEWS strap with a single admin-set headline
 *   exclusive → gold HINTON EXCLUSIVE strap linking to a chosen article
 *
 * Headline / exclusive modes revert cleanly when an admin saves the
 * strap back to "markets" — the live quotes resume on the very next
 * page load. If the chosen exclusive article is unpublished / deleted
 * on the backend, the resolver silently falls back to "markets" too.
 */
export default function NewsTicker() {
  const [cfg, setCfg] = useState(null); // null = loading, then {mode, headline, article_slug?}

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/site-config/ticker");
        if (alive) setCfg(data);
      } catch {
        if (alive) setCfg({ mode: "markets", headline: "" });
      }
    };
    load();
    // Poll every 90s so editors see a manual ticker change reflect
    // across open browser tabs without a hard refresh.
    const t = setInterval(load, 90000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (cfg === null) {
    return (
      <div
        className="bg-black text-white py-2 px-4 text-[11px] font-mono-ed uppercase tracking-[0.25em] text-neutral-500 border-y border-white/10"
        data-testid="news-ticker-loading"
      >
        Loading…
      </div>
    );
  }

  if (cfg.mode === "markets") return <MarketTicker />;
  return <HeadlineTicker mode={cfg.mode} headline={cfg.headline} slug={cfg.article_slug} />;
}

function HeadlineTicker({ mode, headline, slug }) {
  const isBreaking  = mode === "breaking";
  const isExclusive = mode === "exclusive";
  const labelText = isBreaking ? "Breaking" : isExclusive ? "Exclusive" : "Latest";
  // Repeat the headline a few times so the marquee feels populated even
  // for short copy. Separator matches the markets ticker for visual
  // continuity when admin switches modes.
  const repeated = Array.from({ length: 6 }, (_, i) => i);

  // Wrap the whole marquee in a Link when we're in exclusive mode and
  // have a resolved slug — the entire strap becomes clickable so
  // readers can jump straight to the piece.
  const wrapperCls = `${
    isBreaking  ? "bg-[var(--hinton-red)] text-white" :
    isExclusive ? "bg-[var(--hinton-gold)] text-black" :
                  "bg-black text-white"
  } border-y border-white/10 overflow-hidden relative`;
  const labelCls = `absolute left-0 top-0 bottom-0 z-10 flex items-center px-4 text-[10px] font-bold uppercase tracking-[0.25em] ${
    isBreaking  ? "bg-black text-white" :
    isExclusive ? "bg-black text-white" :
                  "bg-[var(--hinton-red)] text-white"
  }`;
  const dotCls = `ml-3 w-1.5 h-1.5 rounded-full bg-white ${isBreaking ? "animate-pulse" : "opacity-60"}`;

  const content = (
    <>
      <div className={labelCls} data-testid="news-ticker-label">
        <span className="hidden sm:inline">Hinton</span>
        <span className="sm:ml-2">{labelText}</span>
        <span className={dotCls} />
      </div>
      <div className="pl-[150px] sm:pl-[180px]">
        <div className="marquee-track py-2.5 will-change-transform" style={{ animationDuration: "60s" }}>
          {repeated.map((i) => (
            <span
              key={i}
              className="px-6 text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.18em] whitespace-nowrap inline-flex items-center gap-3"
            >
              <span>{headline}</span>
              <span className={isExclusive ? "text-black/40" : "text-white/40"}>|</span>
            </span>
          ))}
        </div>
      </div>
    </>
  );

  if (isExclusive && slug) {
    return (
      <Link
        to={`/article/${slug}`}
        className={`${wrapperCls} block hover:brightness-95 transition-[filter]`}
        data-testid="news-ticker"
        data-mode={mode}
      >
        {content}
      </Link>
    );
  }
  return (
    <div className={wrapperCls} data-testid="news-ticker" data-mode={mode}>
      {content}
    </div>
  );
}
